import { SESSION_PROPOSALS_ENABLED } from "../../utils/featureFlags";
import React, { useState, useEffect, useCallback } from "react";
import { DndContext, useDraggable, DragOverlay } from '@dnd-kit/core';
import {
  getSessionsByDashboard,
  getSessionDashboardById,
  getDashboardRoster,
  addDogToRoster,
  getDogsByTrainer,
  planDog,
  unplanDog,
  getDashboardIcalUrl,
  RosterDog,
  SessionDashboard,
} from "../../utils/api";
import { useEventStream } from "../../utils/useEventStream";
import { Session, Dog, PopulatedTrainer } from "../../types";
import { toast } from 'sonner';
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { useAuth } from "../../contexts/AuthContext";
import { SessionsList } from "../session/SessionsList";
import {
  //Lucide-react icons
  ArrowLeft,
  Plus,
  CalendarDays,
  List,
  RefreshCw,
  Share2,
  CalendarPlus,
  GripVertical,
  UserPlus,
} from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "../ui/toggle-group";
import { useViewMode } from "../../contexts/ViewModeContext";
import { SessionForm } from "../session/SessionForm";
import { SessionCalendar } from "../session/SessionCalendar";
import { BatchSessionForm } from "../session/BatchSessionForm";
import { InviteLinkDialog } from "../invitations/InviteLinkDialog";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { Input } from "../ui/input";

const OwnerChip = ({ name }: { name?: string }) =>
  name ? <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground truncate max-w-[9rem]">{name}</span> : null;

const DraggableDog = ({ dog, disabled }: { dog: RosterDog; disabled?: boolean }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: dog.dogId,
    data: { dog },
    disabled,
  });
  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
  } : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`p-2 bg-card border rounded-md flex items-center gap-2 shadow-sm touch-none ${disabled ? "" : "cursor-grab"} ${isDragging ? "opacity-60" : ""}`}
    >
      {!disabled && <GripVertical className="h-4 w-4 text-muted-foreground shrink-0" />}
      <Avatar className="h-8 w-8">
        <AvatarImage src={dog.profilePhoto} />
        <AvatarFallback>{dog.name.substring(0, 2)}</AvatarFallback>
      </Avatar>
      <div className="flex flex-col min-w-0 gap-0.5">
        <span className="font-medium text-sm truncate">{dog.name}</span>
        <OwnerChip name={dog.owner?.username} />
      </div>
    </div>
  );
};

const AddDogPicker = ({ dashboardId, rosterIds, onAdded }: { dashboardId: string; rosterIds: Set<string>; onAdded: () => void }) => {
  const { dogs: ownDogs } = useAuth();
  const [open, setOpen] = useState(false);
  const [candidates, setCandidates] = useState<Dog[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    getDogsByTrainer()
      .catch(() => [] as Dog[])
      .then(followed => {
        const byId = new Map<string, Dog>();
        [...(ownDogs || []), ...followed].forEach(d => byId.set(d._id, d));
        setCandidates(Array.from(byId.values()));
      });
  }, [open, ownDogs]);

  const available = candidates.filter(d => !rosterIds.has(d._id));

  const add = async (dogId: string) => {
    setBusy(true);
    try {
      await addDogToRoster(dashboardId, dogId);
      toast.success("Chien ajouté au groupe.");
      onAdded();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Impossible d'ajouter ce chien.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1">
      <Button variant="outline" size="sm" className="w-full" onClick={() => setOpen(o => !o)}>
        <Plus className="mr-2 h-4 w-4" /> Ajouter un chien
      </Button>
      {open && (
        <div className="rounded-md border p-1 max-h-48 overflow-y-auto">
          {available.length === 0 ? (
            <p className="p-2 text-xs text-muted-foreground">Aucun chien disponible. Invitez les maîtres avec un lien.</p>
          ) : available.map(d => (
            <button key={d._id} type="button" disabled={busy} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted" onClick={() => add(d._id)}>
              <Plus className="h-3 w-3" /> {d.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const AvailableDogsList = ({ dogs, canEdit, onInvite, dashboardId, onChanged }: { dogs: RosterDog[]; canEdit: boolean; onInvite: () => void; dashboardId: string; onChanged: () => void }) => {
  const [filter, setFilter] = useState('');
  const needle = filter.trim().toLowerCase();
  const filteredDogs = dogs.filter(dog =>
    !needle || dog.name.toLowerCase().includes(needle) || (dog.owner?.username || "").toLowerCase().includes(needle)
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-base">
          Chiens du groupe <Badge variant="secondary">{dogs.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <Input placeholder="Chien ou maître..." value={filter} onChange={(e) => setFilter(e.target.value)} />
        {canEdit && <AddDogPicker dashboardId={dashboardId} rosterIds={new Set(dogs.map(d => d.dogId))} onAdded={onChanged} />}
        {canEdit && SESSION_PROPOSALS_ENABLED && (
          <Button variant="outline" size="sm" className="w-full" onClick={onInvite}>
            <UserPlus className="mr-2 h-4 w-4" /> Inviter des maîtres
          </Button>
        )}
        {dogs.length === 0 && <p className="text-xs text-muted-foreground">Aucun chien. Invitez des maîtres avec un lien ou un code pour qu'ils rejoignent le groupe.</p>}
        {dogs.length > 0 && filteredDogs.length === 0 && <p className="text-xs text-muted-foreground">Aucun résultat.</p>}
        {filteredDogs.map(dog => <DraggableDog key={dog.dogId} dog={dog} disabled={!canEdit} />)}
      </CardContent>
    </Card>
  );
};

interface DashboardDetailProps {
  dashboardId: string;
  onBack: () => void;
  onNavigateToSession: (sessionId: string) => void;
}

const byStart = (a: Session, b: Session) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime();

export const DashboardDetail: React.FC<DashboardDetailProps> = ({
  dashboardId,
  onBack,
}) => {
  const { user } = useAuth();
  const { setSelectedDogId } = useViewMode();
  const [roster, setRoster] = useState<RosterDog[]>([]);
  const [dashboardTrainers, setDashboardTrainers] = useState<PopulatedTrainer[]>([]);
  const [dashboard, setDashboard] = useState<SessionDashboard | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isBatchFormOpen, setIsBatchFormOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isSessionFormOpen, setIsSessionFormOpen] = useState(false);
  const [sessionToEdit, setSessionToEdit] = useState<Session | null>(null);
  const [initialDateForNewSession, setInitialDateForNewSession] = useState<
    Date | undefined
  >(undefined);  
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [viewType, setViewType] = useState<"list" | "calendar">("list");

  const fetchRoster = useCallback(async () => {
    try {
      setRoster(await getDashboardRoster(dashboardId));
    } catch (error) {
      console.error("Error fetching roster:", error);
    }
  }, [dashboardId]);

  const fetchData = async () => {
    try {
      const [dashboardData, sessionsData] = await Promise.all([
        getSessionDashboardById(dashboardId),
        getSessionsByDashboard(dashboardId),
        fetchRoster(),
      ]);
      setDashboard(dashboardData);
      setDashboardTrainers(dashboardData.trainerIds as PopulatedTrainer[]);
      setSessions(sessionsData.sort(byStart));
    } catch (error) {
      toast.error("Impossible de charger les détails du tableau de bord.");
      console.error("Error fetching dashboard details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dashboardId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEventStream(dashboardId, {
    "session:created": (newSession: Session) =>
      setSessions((prev) => (prev.some(s => s._id === newSession._id) ? prev : [...prev, newSession].sort(byStart))),
    "sessions:created": (created: Session[]) =>
      setSessions((prev) => [...prev, ...created.filter(c => !prev.some(s => s._id === c._id))].sort(byStart)),
    "session:updated": (updated: Session) => {
      setSessions((prev) => prev.map((s) => (s._id === updated._id ? updated : s)));
      setSelectedSession((prev) => (prev?._id === updated._id ? updated : prev));
    },
    "session:deleted": (data: { sessionId: string }) =>
      setSessions((prev) => prev.filter((s) => s._id !== data.sessionId)),
    "roster:changed": () => { fetchRoster(); },
    "dashboard:updated": () => { getSessionDashboardById(dashboardId).then(setDashboard).catch(() => undefined); },
  });

  const handleDogClick = (dogId: string) => {
    setSelectedDogId(dogId);
  };

  const handleSessionCreated = (newSession: Session) => {
    setSessions((prev) => (prev.some(s => s._id === newSession._id) ? prev : [...prev, newSession].sort(byStart)));
  };

  const handleSessionUpdated = (updatedSession: Session) => {
    setSessions((prev) =>
      prev.map((s) => (s._id === updatedSession._id ? updatedSession : s))
    );
    if (selectedSession?._id === updatedSession._id) setSelectedSession(updatedSession);
  };

  const openCreateSessionForm = (date?: Date) => {
    setSessionToEdit(null);
    setInitialDateForNewSession(date);
    setIsSessionFormOpen(true);
  };

  const handleSelectSession = (sessionId: string) => {
    const session = sessions.find(s => s._id === sessionId);
    if (session) setSelectedSession(session);
  };

  const handleDragStart = (event: any) => {
    setActiveDragId(event.active.id);
  };

  const handleDragEnd = async (event: any) => {
    const { over, active } = event;
    setActiveDragId(null);
    if (!over || !active) return;

    const dogId = active.id;
    const sessionId = over.id;

    const session = sessions.find(s => s._id === sessionId);
    if (!session) return;

    if (session.plannedDogs.some(pd => pd.dog?._id === dogId)) {
      toast.info("Ce chien est déjà dans la session.");
      return;
    }

    try {
      const updatedSession = await planDog(sessionId, dogId);
      handleSessionUpdated(updatedSession);
      toast.success(`Chien ajouté à la session du ${new Date(updatedSession.startTime).toLocaleDateString()}.`);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Erreur lors de l'ajout du chien à la session.");
    }
  };

  const handleRemoveDogFromSession = async (sessionId: string, dogId: string) => {
    const session = sessions.find(s => s._id === sessionId);
    const dogName = session?.plannedDogs.find(pd => pd.dog?._id === dogId)?.dog.name || "le chien";
    try {
      handleSessionUpdated(await unplanDog(sessionId, dogId));
      toast.success(`${dogName} a été retiré de la session.`);
    } catch (error) {
      toast.error(`Erreur lors du retrait de ${dogName}.`);
    }
  };

  const copyCalendarLink = async () => {
    try {
      await navigator.clipboard.writeText(await getDashboardIcalUrl(dashboardId));
      toast.success("Lien du calendrier copié : ajoutez-le à votre agenda (iCal).");
    } catch (error) {
      toast.error("Impossible de générer le lien du calendrier.");
    }
  };

  const draggedDog = activeDragId ? roster.find(d => d.dogId === activeDragId) : null;
  const trainerDogs = roster.map(r => ({ _id: r.dogId, name: r.name, profilePhoto: r.profilePhoto, ownerIds: [] as string[] }) as Dog);
  if (loading || !dashboard || !user) return <div>Chargement du tableau de bord...</div>;

  const isOwner = dashboard.ownerId === user._id;
  const userPermission = dashboard.trainerIds.find((t) => t.userId._id === user._id)?.permission;
  const canEdit = isOwner || userPermission === 'editor';
  const upcomingSessions = sessions.filter(s => new Date(s.startTime) > new Date()).length;
  const completedSessions = sessions.length - upcomingSessions;
  const publishedSessions = sessions.filter(s => s.status === 'published').length;

  return (
    <div className="h-full w-full flex flex-col">
      {/* Header Section */}
      <header className="p-4 border-b bg-card">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button onClick={onBack} variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold">{dashboard.title}</h1>
                {!isOwner && <Badge variant="outline">{userPermission === 'editor' ? 'Éditeur' : 'Lecteur'}</Badge>}
              </div>
              <p className="text-sm text-muted-foreground">{dashboard.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ToggleGroup type="single" value={viewType} onValueChange={(value: "list" | "calendar") => value && setViewType(value)}>
              {viewType === "calendar" && <ToggleGroupItem value="list" aria-label="List view"><List className="h-4 w-4" /></ToggleGroupItem>}
              {viewType === "list" && <ToggleGroupItem value="calendar" aria-label="Calendar view"><CalendarDays className="h-4 w-4" /></ToggleGroupItem>}
            </ToggleGroup>
            <Button variant="outline" size="icon" onClick={copyCalendarLink} title="Copier le lien du calendrier (iCal)" aria-label="Copier le lien du calendrier">
              <CalendarPlus className="h-4 w-4" />
            </Button>
            {canEdit && <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsBatchFormOpen(true)}>Créer en lot</Button>
              <Button size="lg" onClick={() => openCreateSessionForm()}><Plus className="mr-2 h-4 w-4" /> Nouvelle Session</Button>
            </div>}
          </div>
        </div>
        <div className="flex items-center gap-4 mt-2 pl-14 text-sm text-muted-foreground">
          <span>{sessions.length} sessions</span>
          <Badge variant="outline">{upcomingSessions} à venir</Badge>
          <Badge variant="outline">{completedSessions} terminées</Badge>
          {SESSION_PROPOSALS_ENABLED && <Badge variant="outline">{publishedSessions} publiées</Badge>}
        </div>
      </header>

      {/* Content Area */}
      <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <main className="flex-1 p-6 overflow-hidden grid grid-cols-1 lg:grid-cols-6 gap-6">
          <div className="lg:col-span-5 overflow-y-auto">
            {viewType === "list" && (
              <SessionsList
                sessions={sessions}
                onDogClick={handleDogClick}
                onSessionClick={handleSelectSession}
                selectedSessionId={selectedSession?._id}
                onSessionUpdate={handleSessionUpdated}
                canEdit={canEdit}
                onRemoveDog={handleRemoveDogFromSession}
                dashboardTrainers={dashboardTrainers.map(t => t.userId)}
              />
            )}
            {viewType === "calendar" && (
              <SessionCalendar sessions={sessions} onSessionClick={(session) => setSelectedSession(session)} onEmptySlotClick={openCreateSessionForm} />
            )}
          </div>
          <div className="lg:col-span-1 overflow-y-auto">
            <AvailableDogsList dogs={roster} canEdit={canEdit} onInvite={() => setIsInviteOpen(true)} dashboardId={dashboardId} onChanged={fetchRoster} />
          </div>
        </main>
        <DragOverlay>
          {draggedDog ? (
            <DraggableDog dog={draggedDog} />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Footer Row */}
      <footer className="p-2 border-t text-xs text-muted-foreground flex justify-between items-center">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-3 w-3" />
          <span>Dernière sync: {new Date(dashboard.lastSyncedAt || Date.now()).toLocaleString()}</span>
        </div>
        {isOwner && (
          <div className="flex items-center gap-2">
            <Share2 className="h-3 w-3" />
            <span>Partagé avec {dashboard.trainerIds.length - 1} éducateur(s)</span>
          </div>
        )}
      </footer>
      <InviteLinkDialog
        isOpen={isInviteOpen}
        onOpenChange={setIsInviteOpen}
        title="Inviter dans le groupe"
        options={[
          ...(SESSION_PROPOSALS_ENABLED ? [{ type: 'dashboard-roster' as const, targetId: dashboardId, label: "Maître avec son chien (rejoint le groupe)" }] : []),
          ...(isOwner ? [
            { type: 'dashboard' as const, targetId: dashboardId, label: "Éducateur (partage du tableau de bord)", permission: true },
          ] : []),
        ]}
      />
      <SessionForm
        isOpen={isSessionFormOpen}
        onOpenChange={setIsSessionFormOpen}
        dashboardId={dashboardId}
        trainerDogs={trainerDogs}
        sessionToEdit={sessionToEdit}
        onSessionCreated={handleSessionCreated}
        onSessionUpdated={handleSessionUpdated}
        initialDate={initialDateForNewSession}
      />
      <BatchSessionForm
        isOpen={isBatchFormOpen}
        onOpenChange={setIsBatchFormOpen}
        dashboardId={dashboardId}
        dashboardTrainers={dashboardTrainers.map(t => t.userId)}
        onSessionsCreated={fetchData}
      />
    </div>
  );
};